import { RocheUIHeader, RocheUILanguageSwitchConfig } from '@repo/roche-ui';
// As part of https://roche-ds.atlassian.net/browse/EXTCWP-381 we should untangle the services.
// The header should not import anything from @rocheglobal/component-library-dot-com.
import { HistoryService } from '@rocheglobal/component-library-dot-com/src/services/history';
import { TrackingService } from '@rocheglobal/component-library-dot-com/src/services/tracking';
import * as React from 'react';
import { FC, useEffect, useState } from 'react';
import { trackClick } from '../../helpers/roche-language-tracking';
import { useTranslation } from '../../hooks/use-translation';
import { NavigationStory, PageContextStory, Story } from '../../services/build/types';
import { AlternateLanguageLink } from '../../services/dom/types';
import { LanguageService } from '../../services/language';
import { NavigationService } from '../../services/navigation';
import { StringService } from '../../services/string';
import { AdobeTracking } from '../../tracking-services';

type HeaderSectionProps = {
  home: Story<PageContextStory>;
  storyContent: PageContextStory;
  navigationContent: NavigationStory;
  alternates: AlternateLanguageLink[];
};

const defaultTracking = {
  event: 'navigation_click',
  component_name: 'roche-navigation',
};

const defaultAdobeTrackingData = {
  position: 'Navigation',
  type: 'Link',
  socialMedia: '',
  socialInteraction: '',
};

const trackLinkClick = (event, linkText: string): void => {
  const targetLink = event.target?.getAttribute('href')
    ? event.target?.getAttribute('href')
    : event.target?.parentElement?.getAttribute('href');

  TrackingService.push({
    ...defaultTracking,
    additionalParameters: {
      current_location: HistoryService.getActiveUrl().pathname,
      link_text: linkText,
    },
    value: targetLink,
  });

  AdobeTracking(
    {
      ...defaultAdobeTrackingData,
      url: targetLink,
      innerText: linkText,
    },
    'defaultLinkClick',
  );
};

export const HeaderSection: FC<HeaderSectionProps> = ({ home, storyContent, navigationContent, alternates }) => {
  const [currentPage, setCurrentPage] = useState('');
  const homeLabelFallback = useTranslation('home');
  const searchLabelFallback = useTranslation('search');
  const worldWideLabelFallback = useTranslation('worldwide');
  const exploreLabelFallback = useTranslation('explore');
  const skipLabelFallback = useTranslation('skip to content');

  const pageHasAlternateLanguages = alternates.length > 0;
  const currentLocale = LanguageService.getActiveLanguage();
  const sanitizedAlternates = alternates.map((alt) => ({ ...alt, url: StringService.sanitizeUrl(`/${alt.url}`) }));
  const sortedAlternates: AlternateLanguageLink[] = [
    ...sanitizedAlternates,
    { locale: currentLocale, url: currentPage },
  ]
    .filter((alternate) => Boolean(alternate.locale))
    .sort((firstItem, secondItem) => firstItem.locale.localeCompare(secondItem.locale));

  const languageSwitchConfig: RocheUILanguageSwitchConfig = pageHasAlternateLanguages
    ? {
        alternates: sortedAlternates,
        translations: {
          translatedLocale: Object.fromEntries(
            sortedAlternates.map((alternate) => [alternate.locale, useTranslation(alternate.locale)]),
          ),
        },
        handleClickTracking: (toLocale) => trackClick(currentLocale, toLocale),
        currentLocale,
      }
    : undefined;

  useEffect(() => {
    setCurrentPage(NavigationService.getCurrentPagePath());
  }, []);

  const primaryNav = navigationContent?.primary_navigation.map((navItem) => ({
    ...navItem,
    url: navItem.url ? NavigationService.getParsedNavigationLink(navItem.url) : null,
    onClick: (event) => trackLinkClick(event, navItem.text),
    nestedLinks: navItem.nested_links?.map((nestedLink) => ({
      ...nestedLink,
      url: NavigationService.getParsedNavigationLink(nestedLink.url),
      onClick: (event) => trackLinkClick(event, nestedLink.text),
    })),
  }));

  const secondaryNav = navigationContent?.secondary_navigation.map((navItem) => ({
    ...navItem,
    url: NavigationService.getParsedNavigationLink(navItem.url),
    onClick: (event) => trackLinkClick(event, navItem.text),
  }));

  const homeLabel = navigationContent?.home_label ?? homeLabelFallback;
  const searchLabel = navigationContent?.search_label ?? searchLabelFallback;
  const worldWideLabel = navigationContent?.world_wide_label ?? worldWideLabelFallback;
  const exploreLabel = navigationContent?.explore_label ?? exploreLabelFallback;
  const skipToContentLabel = navigationContent?.skip_to_content_label ?? skipLabelFallback;

  const homeLink = {
    label: homeLabel,
    url: LanguageService.getHomePageUrl(home),
    onClick: (event) => trackLinkClick(event, homeLabel),
  };
  const searchLink = {
    label: searchLabel,
    url: NavigationService.getParsedNavigationLink(navigationContent?.global_search_link),
    onClick: (event) => trackLinkClick(event, searchLabel),
  };
  const worldWideLink = {
    label: worldWideLabel,
    url: NavigationService.getParsedNavigationLink(navigationContent?.roche_world_wide_link),
    onClick: (event) => trackLinkClick(event, worldWideLabel),
  };

  return (
    <>
      <roche-animation-manager />

      {!storyContent?.remove_header && navigationContent && (
        <RocheUIHeader
          homeLink={homeLink}
          isFixed={storyContent?.fixed_header}
          searchLink={searchLink}
          worldWideLink={worldWideLink}
          exploreLabel={exploreLabel}
          openNavLabel={navigationContent?.open_navigation_label}
          closeNavLabel={navigationContent?.close_navigation_label}
          currentPage={currentPage}
          primaryNav={primaryNav}
          primaryNavLabel={navigationContent?.primary_navigation_label}
          secondaryNav={secondaryNav}
          secondaryNavLabel={navigationContent?.secondary_navigation_label}
          skipToContentLabel={skipToContentLabel}
          languageSwitchConfig={languageSwitchConfig}
        />
      )}
    </>
  );
};
